/**
 * Google Analytics 4 Integration
 *
 * Setup:
 * 1. Crea una cuenta en Google Analytics: https://analytics.google.com
 * 2. Obtén tu MEASUREMENT_ID (formato: G-XXXXXXXXXX)
 * 3. Añade VITE_GA_MEASUREMENT_ID a tus variables de entorno
 */

import { logger } from './logger';

const analyticsLogger = logger.withContext('Analytics');

// Declaración de tipos para gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

/**
 * Inicializa Google Analytics
 * Solo se ejecuta en producción si existe el Measurement ID
 */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID || import.meta.env.DEV) {
    analyticsLogger.info('GA deshabilitado en desarrollo');
    return;
  }

  // Cargar script de Google Analytics
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Inicializar dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
  });

  analyticsLogger.info('Google Analytics inicializado', { measurementId: GA_MEASUREMENT_ID });
};

/**
 * Trackea un evento personalizado
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    category?: string;
    label?: string;
    value?: number;
    [key: string]: unknown;
  }
) => {
  if (!window.gtag) return;

  window.gtag('event', eventName, eventParams);

  analyticsLogger.debug('Evento trackeado', { eventName, eventParams });
};

/**
 * Trackea una vista de página
 */
export const trackPageView = (path: string, title?: string) => {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });

  analyticsLogger.debug('Vista de página trackeada', { path, title });
};

/**
 * Eventos de negocio específicos
 */
export const analytics = {
  // Autenticación
  signUp: (method: string) =>
    trackEvent('sign_up', { method }),

  login: (method: string) =>
    trackEvent('login', { method }),

  // Reservas
  bookingStarted: (serviceId: string, serviceName: string) =>
    trackEvent('booking_started', {
      category: 'Booking',
      service_id: serviceId,
      service_name: serviceName,
    }),

  bookingCompleted: (serviceId: string, price: number) =>
    trackEvent('booking_completed', {
      category: 'Booking',
      service_id: serviceId,
      value: price,
    }),

  // Servicios
  serviceCreated: (serviceName: string, price: number) =>
    trackEvent('service_created', {
      category: 'Service',
      service_name: serviceName,
      value: price,
    }),

  serviceViewed: (serviceId: string, serviceName: string) =>
    trackEvent('service_viewed', {
      category: 'Service',
      service_id: serviceId,
      service_name: serviceName,
    }),

  // Búsqueda
  search: (query: string, resultsCount: number) =>
    trackEvent('search', {
      search_term: query,
      results_count: resultsCount,
    }),

  // Suscripciones
  subscriptionStarted: (planName: string) =>
    trackEvent('subscription_started', {
      category: 'Subscription',
      plan_name: planName,
    }),

  subscriptionCompleted: (planName: string, price: number) =>
    trackEvent('purchase', {
      category: 'Subscription',
      plan_name: planName,
      value: price,
      currency: 'EUR',
    }),

  // PWA
  pwaInstalled: () =>
    trackEvent('pwa_installed', {
      category: 'PWA',
    }),

  // Errores
  error: (errorMessage: string, errorLocation: string) =>
    trackEvent('error', {
      category: 'Error',
      error_message: errorMessage,
      error_location: errorLocation,
    }),
};